import React, { useState } from "react";
import { GaugeTile } from "../../Components/Common/NorthStarViz";
import { KpiCard } from "../../Components/Common/DashboardWidgets";
import { Skeleton } from "../../Components/Common/LoadingStates";
import { useCachedRequest } from "../../helpers/cachedFetch";
import { usePermission } from "../../helpers/usePermission";
import {
  getPrDisciplineSummary,
  getPrDisciplineOpenPrs,
  postPrDisciplineSync,
} from "../../helpers/fakebackend_helper";

// Warn/alarm colours are literals on purpose: --bl-warn is referenced by several
// components but is NOT actually defined as a CSS variable (see CLAUDE.md), so
// relying on it here would render an unstyled chip.
const WARN = "#f3af4c";
const ALARM = "#ff5d6c";
const OK = "#2BD56F";

function fmtAge(hours) {
  if (hours == null) return "—";
  if (hours < 48) return `${Math.round(hours)}h`;
  const d = hours / 24;
  return d < 90 ? `${Math.round(d)}d` : `${(d / 30.44).toFixed(1)}mo`;
}

function fmtWhen(iso) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return String(iso);
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

/** Explains a blank tile instead of rendering a confident zero. */
function AwaitingBackend({ note }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: "1.5rem", color: "var(--bl-text-soft)", textAlign: "center" }}>
      <i className="ri-server-line" style={{ fontSize: 26, opacity: 0.5 }} />
      <span style={{ fontSize: 13 }}>Awaiting backend — no data yet</span>
      {note && <span style={{ fontSize: 11, opacity: 0.8 }}>{note}</span>}
    </div>
  );
}

/**
 * A poller that has quietly died is worse than a blank tile: the percentage
 * stays plausible while drifting arbitrarily far from reality. Say so loudly.
 */
function FreshnessBanner({ freshness, onSync, syncing, canSync }) {
  if (!freshness) return null;
  const bad = freshness.status !== "ok" || freshness.stale;
  return (
    <div
      className="bl-glass"
      style={{
        padding: "0.7rem 0.9rem",
        marginBottom: "1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        borderLeft: `3px solid ${bad ? WARN : OK}`,
      }}
    >
      <div style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>
        {freshness.status === "never_run" ? (
          <>Never polled. Set <code>GITHUB_TOKEN</code> and <code>GITHUB_REPOS</code>, then run a sync.</>
        ) : (
          <>
            Last polled {fmtWhen(freshness.started_at)} — <strong style={{ color: bad ? WARN : OK }}>{freshness.status}</strong>
            {freshness.stale && " (stale)"}
            {freshness.error_message ? <> · {freshness.error_message}</> : null}
          </>
        )}
      </div>
      {canSync && (
        <button className="bl-btn-primary" onClick={onSync} disabled={syncing} style={{ padding: "0.35rem 0.9rem", fontSize: 12, flexShrink: 0 }}>
          {syncing ? "Syncing…" : "Refresh"}
        </button>
      )}
    </div>
  );
}

function RepoTable({ repos }) {
  if (!repos || repos.length === 0) return null;
  return (
    <div className="bl-glass" style={{ padding: "1.1rem 1.2rem", marginTop: "1rem" }}>
      <div style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
        By repo and branch
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 520 }}>
          <thead>
            <tr style={{ color: "var(--bl-text-soft)", textAlign: "left" }}>
              <th style={{ padding: "6px 8px", fontWeight: 500 }}>Repo / branch</th>
              <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Commits</th>
              <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Via PR</th>
              <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Direct</th>
              <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Ratio</th>
            </tr>
          </thead>
          <tbody>
            {repos.map((r) => (
              <React.Fragment key={r.repo}>
                <tr style={{ borderTop: "1px solid var(--bl-line)" }}>
                  <td style={{ padding: "8px", fontWeight: 600 }}>{r.repo}</td>
                  <td style={{ padding: "8px", textAlign: "right", fontFamily: "var(--bl-font-numeric)" }}>{r.commits_total}</td>
                  <td style={{ padding: "8px", textAlign: "right", fontFamily: "var(--bl-font-numeric)" }}>{r.commits_via_merged_pr}</td>
                  <td style={{ padding: "8px", textAlign: "right", fontFamily: "var(--bl-font-numeric)", color: r.direct_pushes > 0 ? ALARM : "inherit" }}>{r.direct_pushes}</td>
                  <td style={{ padding: "8px", textAlign: "right", fontFamily: "var(--bl-font-numeric)" }}>
                    {r.ratio == null ? "—" : `${Math.round(r.ratio * 100)}%`}
                  </td>
                </tr>
                {(r.branches || []).map((b) => (
                  <tr key={r.repo + b.branch} style={{ color: "var(--bl-text-soft)" }}>
                    <td style={{ padding: "4px 8px 4px 24px" }}>{b.branch}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", fontFamily: "var(--bl-font-numeric)" }}>{b.commits_total}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", fontFamily: "var(--bl-font-numeric)" }}>{b.commits_via_merged_pr}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", fontFamily: "var(--bl-font-numeric)" }}>{b.direct_pushes}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", fontFamily: "var(--bl-font-numeric)" }}>
                      {b.ratio == null ? "—" : `${Math.round(b.ratio * 100)}%`}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OpenPrList({ prs, alarmHours }) {
  const [expanded, setExpanded] = useState(false);
  if (!prs || prs.length === 0) return null;
  const shown = expanded ? prs : prs.slice(0, 5);
  return (
    <div className="bl-glass" style={{ padding: "1.1rem 1.2rem", marginTop: "1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Open pull requests ({prs.length})
        </span>
        {prs.length > 5 && (
          <button onClick={() => setExpanded(!expanded)} style={{ background: "transparent", border: 0, color: "var(--bl-accent)", cursor: "pointer", fontSize: 12 }}>
            {expanded ? "Show less" : `Show all ${prs.length}`}
          </button>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {shown.map((p) => {
          const breached = p.waiting_hours > alarmHours;
          return (
            <div key={`${p.repo}#${p.number}`} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
              <span style={{ fontFamily: "var(--bl-font-numeric)", color: breached ? ALARM : "var(--bl-text-soft)", minWidth: 54, textAlign: "right" }}>
                {fmtAge(p.waiting_hours)}
              </span>
              <a href={p.html_url} target="_blank" rel="noreferrer" style={{ color: "var(--bl-text-strong)", textDecoration: "none", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.repo.split("/").pop()} #{p.number} · {p.title}
              </a>
              <span style={{ fontSize: 11, color: "var(--bl-text-soft)", flexShrink: 0 }}>
                {p.review_count === 0 ? "no review" : `${p.review_count} review${p.review_count === 1 ? "" : "s"}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PrDisciplinePanel = () => {
  const can = usePermission();
  const [syncing, setSyncing] = useState(false);
  const [bump, setBump] = useState(0);

  const { data: summary, loading } = useCachedRequest({
    key: `pr-discipline-summary-${bump}`,
    fetcher: () => getPrDisciplineSummary().catch(() => null),
    deps: [bump],
  });
  const { data: openData } = useCachedRequest({
    key: `pr-discipline-open-${bump}`,
    fetcher: () => getPrDisciplineOpenPrs().catch(() => null),
    deps: [bump],
  });

  const onSync = async () => {
    setSyncing(true);
    try {
      await postPrDisciplineSync();
    } catch (_) {
      /* the freshness banner reports the resulting run status */
    } finally {
      setSyncing(false);
      setBump((n) => n + 1);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "grid", gap: "1rem" }}>
        <Skeleton height={64} />
        <Skeleton height={140} />
      </div>
    );
  }

  if (!summary || !summary.headline) {
    return <AwaitingBackend note="GET /metrics/pr-discipline/summary" />;
  }

  const h = summary.headline;
  const s = summary.supporting || {};
  const oldest = s.oldest_waiting_pr;
  const board = s.board_tasks_vs_prs_opened || {};

  return (
    <div>
      <FreshnessBanner
        freshness={summary.freshness}
        onSync={onSync}
        syncing={syncing}
        canSync={can("pr_discipline.sync")}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem" }}>
        <GaugeTile
          label={`${h.label} · ${summary.window_days}d`}
          current={h.ratio}
          target={h.target}
          unit="ratio"
          status={h.status}
        />
        <KpiCard
          label="Direct pushes"
          value={Number(h.direct_pushes || 0).toLocaleString()}
          sub={`of ${Number(h.commits_total || 0).toLocaleString()} commits`}
          icon="ri-git-commit-line"
          accent={h.direct_pushes > 0 ? ALARM : OK}
          accentRgb={h.direct_pushes > 0 ? "255,93,108" : "43,213,111"}
        />
        <KpiCard
          label="Awaiting review"
          value={Number(s.open_prs_awaiting_review || 0).toLocaleString()}
          sub={`${s.merged_without_review || 0} of ${s.merged_total || 0} merged with no review`}
          icon="ri-git-pull-request-line"
          accent={s.open_prs_awaiting_review > 0 ? WARN : OK}
          accentRgb={s.open_prs_awaiting_review > 0 ? "243,175,76" : "43,213,111"}
        />
        <KpiCard
          label="Oldest waiting PR"
          value={oldest ? fmtAge(oldest.hours) : "—"}
          sub={oldest ? `${oldest.repo.split("/").pop()} #${oldest.number} · alarm over ${s.review_alarm_hours}h` : "no open PRs"}
          icon="ri-time-line"
          accent={oldest && oldest.breached ? ALARM : OK}
          accentRgb={oldest && oldest.breached ? "255,93,108" : "43,213,111"}
        />
        <KpiCard
          label="Board coverage"
          value={`${board.on_board || 0}/${board.prs_opened || 0}`}
          sub={board.anomaly ? "review tasks are not being created" : "PRs opened with a board task"}
          icon="ri-kanban-view"
          accent={board.anomaly ? WARN : OK}
          accentRgb={board.anomaly ? "243,175,76" : "43,213,111"}
        />
      </div>

      <RepoTable repos={summary.repos} />
      <OpenPrList prs={openData && openData.open_prs} alarmHours={s.review_alarm_hours || 48} />
    </div>
  );
};

export default PrDisciplinePanel;
